import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Immigration data hooks.
//
// - useImmigrationCountries: reference data, shared across users.
//   Seeded server-side (migration 061). The frontend is owner-gated
//   but the table itself is read-by-any-authenticated for simplicity.
// - useImmigrationUserState: per-user pinned/status/notes per country.
//   Joins with countries client-side because there are only ~5 rows
//   total — a Postgres join would be overkill.

export function useImmigrationCountries() {
  const [countries, setCountries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured) {
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error: err } = await supabase
      .from('immigration_countries')
      .select('*')
      .order('display_order', { ascending: true })
    if (err) setError(err.message)
    else setCountries(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetch() }, [fetch])

  return { countries, loading, error, refetch: fetch }
}

export function useImmigrationUserState() {
  const { user } = useAuth()
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(true)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    const { data } = await supabase
      .from('immigration_user_state')
      .select('*')
      .eq('user_id', user.id)
    setStates(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const stateBySlug = useMemo(() => {
    const map = new Map()
    for (const s of states) map.set(s.country_slug, s)
    return map
  }, [states])

  // Upsert by (user_id, country_slug). Pass only the fields you want
  // to set — anything else keeps its prior value.
  const upsertState = useCallback(async ({ country_slug, ...patch }) => {
    if (!user) return false
    const existing = stateBySlug.get(country_slug)
    const payload = {
      user_id: user.id,
      country_slug,
      ...(existing || {}),
      ...patch,
    }
    delete payload.id
    delete payload.created_at
    delete payload.updated_at
    const { error: err } = await supabase
      .from('immigration_user_state')
      .upsert(payload, { onConflict: 'user_id,country_slug' })
    if (err) return false
    await fetch()
    return true
  }, [user, stateBySlug, fetch])

  return { states, stateBySlug, loading, upsertState, refetch: fetch }
}
